import React from 'react';
import { ChevronDownIcon } from '../Icons';
import styles from './Select.module.css';

export interface SelectOption {
  label: string;
  value: string | number;
  /** Se muestra en la lista pero no se puede elegir (ej. proveedor sin acceso). */
  disabled?: boolean;
  /** Texto auxiliar a la derecha de la opción (ej. "pending access"). */
  hint?: string;
  /**
   * Tono del texto auxiliar.
   *
   * 📌 `warn` existe para un caso concreto: un proveedor que **normalmente sí se puede usar** y está
   * apagado ahora. En gris se lee como "nunca estuvo disponible", que es otra cosa y lleva a pedir
   * credenciales en lugar de prender el interruptor.
   */
  hintTone?: 'muted' | 'warn';
}

interface SelectProps {
  options: SelectOption[];
  value?: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  searchable?: boolean;
  /** Habilita crear un nuevo item (carrier/mode/border). */
  onAddItem?: (name: string, description?: string) => void;
  addLabel?: string;
  withDescription?: boolean;
  /** Tiñe el control con este color (bg suave + texto + borde). Ej. color de status. */
  accentColor?: string;
}

const CloseX: React.FC = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled,
  loading,
  allowClear,
  searchable = true,
  onAddItem,
  addLabel = 'Add',
  withDescription,
  accentColor,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [newDesc, setNewDesc] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    /**
     * 🔴 En fase de captura (19-oct-2026), como `MultiSelect`: el `Modal` detiene el `mousedown` de su panel y en la fase
     * normal el clic no llegaba al documento, así que dentro de un modal (el del Protect Time: *Delay by* y *Reason*) un
     * menú abierto no se cerraba al abrir otro.
     */
    document.addEventListener('mousedown', onOutside, true);
    return () => document.removeEventListener('mousedown', onOutside, true);
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  const pick = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    setQuery('');
  };

  const add = () => {
    if (!newName.trim() || !onAddItem) return;
    onAddItem(newName.trim(), withDescription ? newDesc.trim() : undefined);
    setNewName('');
    setNewDesc('');
    setOpen(false);
  };

  return (
    <div className={[styles.wrap, open ? styles.open : '', loading ? 'dylo-loading-host' : ''].join(' ')} ref={ref}>
      <button
        type="button"
        className={styles.control}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={
          accentColor
            ? {
                background: `${accentColor}22`,
                color: accentColor,
                borderColor: `${accentColor}66`,
                fontWeight: 600,
              }
            : undefined
        }
      >
        <span className={selected ? styles.value : styles.placeholder}>
          {selected ? selected.label : placeholder}
        </span>
        <span className={styles.right} style={accentColor ? { color: accentColor } : undefined}>
          {allowClear && selected && !loading && (
            <span
              className={styles.clear}
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
            >
              <CloseX />
            </span>
          )}
          <span className={styles.chevron}>
            <ChevronDownIcon size={16} />
          </span>
        </span>
      </button>

      {loading && <span className="dylo-loading-sweep dylo-loading-sweep--field" aria-label="Loading" />}

      {open && (
        <div className={styles.menu}>
          {searchable && (
            <input
              className={styles.search}
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          <div className={styles.list}>
            {filtered.length === 0 ? (
              <div className={styles.empty}>No options</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={String(o.value)}
                  type="button"
                  disabled={o.disabled}
                  aria-disabled={o.disabled}
                  className={[
                    styles.option,
                    String(o.value) === String(value) ? styles.optionActive : '',
                    o.disabled ? styles.optionDisabled : '',
                  ].join(' ')}
                  onClick={() => pick(o)}
                >
                  <span>{o.label}</span>
                  {o.hint && (
                    <span
                      className={[
                        styles.optionHint,
                        o.hintTone === 'warn' ? styles.optionHintWarn : '',
                      ].join(' ')}
                    >
                      {o.hint}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {onAddItem && (
            <div className={styles.addRow}>
              <input
                className={styles.addInput}
                placeholder="New name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') add();
                }}
              />
              {withDescription && (
                <input
                  className={styles.addInput}
                  placeholder="Description"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                />
              )}
              <button
                type="button"
                className={styles.option}
                style={{ color: 'var(--dylo-teal-700)', fontWeight: 600 }}
                onClick={add}
              >
                + {addLabel}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
